<template>
    <div id="main">
        <div class="page">
            <div class="tipos-servicos vertical-flow">
                <div class="listing-header">
                    <div class="title-breadcrumb">
                        <h1 class="title">{{ $t('relatorio-km-automatico') }}</h1>
                        <nav class="breadcrumb" aria-label="breadcrumbs">
                            <ul>
                                <li>
                                    <a class="a-inativo">{{ $t('despesas') }}</a>
                                </li>
                                <li>
                                    <a class="a-inativo">{{ $t('relatorios') }}</a>
                                </li>
                                <li>
                                    <a aria-current="page">{{ $t('relatorio-km-automatico') }}</a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>

                <div class="search-and-filter">
                    <div class="columns is-variables is-desktop">
                        <div class="column header-search is-4">
                            <search-bar
                                :clean="limparBusca"
                                :onSearch="debouncedOnSearch"
                                :placeholder="$t('busca')"
                                width="40"
                                tooltipLabel="msg-campo-busca-kmautomatico"
                            />
                        </div>
                        <!-- Data Inicio-->
                        <div class="column is-3">
                            <verified-date-picker
                                :dateType="'startDate'"
                                :value="datainicial"
                                :endDate="datafinal"
                                :title="'data-inicio'"
                                @input="changeStartDate"
                            />
                        </div>
                        <!-- Data Fim-->
                        <div class="column is-3">
                            <verified-date-picker
                                :dateType="'endDate'"
                                :value="datafinal"
                                :startDate="datainicial"
                                :title="'data-fim'"
                                @input="changeEndDate"
                            />
                        </div>

                        <div class="column is-2">
                            <div class="is-mobile mt-2-custom">
                                <b-button
                                    t
                                    class="float-right"
                                    type="is-primary"
                                    icon-right="file-pdf"
                                    @click="getReportPDF()"
                                    v-tippy
                                    :title="$t('msg-tooltip-pdf')"
                                />
                                <b-button
                                    class="mr-2 float-right"
                                    type="is-primary"
                                    icon-right="google-spreadsheet"
                                    @click="getReportCSV()"
                                    v-tippy
                                    :title="$t('msg-tooltip-csv')"
                                />
                                <b-button
                                    class="mr-2 float-right"
                                    type="is-primary"
                                    icon-right="filter"
                                    @click="showFiltros = !showFiltros"
                                    v-tippy
                                    :title="$t('filtros')"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <section class="registry-wrapper mt-4">
                    <container-loader v-if="isLoading && !$store.getters.isLoading" />
                    <empty-table-message class="mt-3" v-if="isEmpty" />
                    <b-table
                        v-else
                        :data="kmsautomaticos"
                        class="data-table"
                        @sort="sortTable"
                        backend-sorting
                    >
                        <b-table-column
                            v-for="f in fields"
                            :key="f.key + '-' + f.colaborador"
                            :field="f.key"
                            :label="f.key == 'deletar' ? '' : $t(f.key)"
                            :class="{ 'excluir-field': f.key == 'deletar' }"
                            :width="f.width"
                            :sortable="f.sortable"
                            v-slot="props"
                        >
                            <div class="has-text-left" v-if="f.key == 'colaborador'">
                                {{ props.row.colaborador }}
                            </div>

                            <!-- 
                                @70209 - Adicionado origem, destino e motivo despesa na lista de km automatico
                                -->
                            <div class="has-text-left" v-else-if="f.key == 'origem'">
                                <a @click="showKmAutomaticoView(props.row.iddespesa)">
                                    {{ props.row.origem }}
                                </a>
                            </div>

                            <div class="has-text-left" v-else-if="f.key == 'destino'">
                                <a @click="showKmAutomaticoView(props.row.iddespesa)">
                                    {{ props.row.destino }}
                                </a>
                            </div>

                            <div v-else-if="f.key == 'motivos-despesas'" class="has-text-left">
                                {{ props.row.despesamotivo }}
                            </div>

                            <div v-else-if="f.key == 'valor'">
                                {{ getDecimal(props.row.valor, null, true) }}
                            </div>

                            <div v-else-if="f.key == 'data-lancamento'">
                                <span v-if="props.row['data-lancamento']">
                                    {{ props.row['data-lancamento'] | moment('L') }}
                                </span>
                            </div>

                            <div v-else-if="f.key == 'situacao'">
                                <b-tag
                                    class="table-tag table-tag-full-width"
                                    :type="isStatusButtonType(props.row.situacao)"
                                    size="is-medium"
                                >
                                    {{ $t(props.row.situacao.toLowerCase()) }}
                                </b-tag>
                            </div>
                        </b-table-column>
                        <template slot="footer">
                            <th v-for="f in fields" :key="f.key + '-' + f.nome" :id="f.key">
                                <span v-if="f.key == 'nome'"
                                    >{{ acertos.length }} {{ $t('registro') }}</span
                                >
                                <span v-if="f.key == 'valor'"
                                    >{{ getDecimal(valorKmsAutomaticos, null, true) }}</span
                                >
                                <span v-else></span>
                            </th>
                        </template>
                    </b-table>
                </section>
                <PaginationViewer
                    class="mb-0"
                    :resetPagination="pagination.resetPagination"
                    :totalItems="pagination.totalRows"
                    :perPageOptions="pagination.perPageOptions"
                    :optionStarted="pagination.optionStarted"
                    @change="updatePagination"
                />
            </div>
        </div>

        <div
            ref="listaFiltros"
            class="filtros-right"
            :class="{ 'hide-filtros-right': !showFiltros }"
            v-show="!isLoading"
        >
            <div class="filtros-container">
                <div class="columns is-multiline">
                    <div class="column is-12">
                        <label v-text="$t('situacao')" />
                        <v-select
                            ref="situacoesInput"
                            label="label"
                            :options="situacoes"
                            v-model="situacao"
                            @input="onCleanSituacoes"
                        >
                            <template slot="option" slot-scope="option">
                                {{ $t(option.label) }}
                            </template>
                            <template slot="selected-option" slot-scope="option">
                                {{ $t(option.label) }}
                            </template>
                            <template slot="no-options">{{ $t('sem-resultados') }}</template>
                        </v-select>
                    </div>

                    <div class="column is-12">
                        <label v-text="$t('empresa')" />
                        <v-select
                            ref="empresasInput"
                            label="label"
                            :options="empresas"
                            v-model="empresa"
                            @input="onCleanEmpresas"
                        >
                        </v-select>
                    </div>

                    <div class="column is-12">
                        <label v-text="$t('colaborador')" />
                        <v-select
                            ref="colaboradoresInput"
                            label="label"
                            :options="colaboradores"
                            v-model="colaborador"
                            @input="onCleanColaboradores"
                        >
                        </v-select>
                    </div>

                    <div class="column is-12">
                        <label v-text="$t('motivos-despesas')" />
                        <v-select
                            ref="motivoDespesasInput"
                            label="label"
                            :options="motivoDespesas"
                            v-model="motivoDespesa"
                            @input="onCleanMotivoDespesas"
                        >
                        </v-select>
                    </div>
                </div>
            </div>
            <div class="filtros-footer">
                <div class="columns is-mobile">
                    <div class="column">
                        <b-button
                            type="is-warning is-silver"
                            icon-right="close"
                            @click="showFiltros = !showFiltros"
                            v-tippy
                            :title="$t('fechar')"
                        />
                    </div>
                    <div class="column">
                        <b-button
                            type="is-warning is-silver"
                            icon-right="backspace"
                            @click="onCleanFilters()"
                            class="float-right"
                            >Limpar</b-button
                        >
                    </div>
                    <div class="column">
                        <b-button
                            type="is-primary"
                            icon-right="filter"
                            @click="timeoutCarregarDados()"
                            class="float-right"
                            >Filtrar</b-button
                        >
                    </div>
                </div>
            </div>
        </div>

        <b-modal :active.sync="isShowingKmAutomatico">
            <div class="bg p-3">
                <KmAutomaticoViewModal :id="idKmAutomatico"></KmAutomaticoViewModal>
            </div>
        </b-modal>
    </div>
</template>

<script>
import RelatorioKmAutomaticoService from '@/services/despesas/RelatorioKmAutomaticoService';
import ContainerLoader from '@/components/content/loader/ContainerLoader';
import Message from '@/models/Message';
import KmAutomaticoViewModal from './KmAutomaticoViewModal.vue';

export default {
    components: {
        ContainerLoader,
        KmAutomaticoViewModal,
    },
    data() {
        return {
            limparBusca: false,
            showFiltros: false,
            situacao: { label: 'todos', id: 2 },
            situacoes: [
                { label: 'todos', id: 2 },
                { label: 'fechados', id: 1 },
                { label: 'abertos', id: 0 },
            ],
            empresa: { label: this.$t('todos'), id: 0 },
            empresas: [{ label: this.$t('todos'), id: 0 }],
            colaborador: { label: this.$t('todos'), id: 0 },
            colaboradores: [{ label: this.$t('todos'), id: 0 }],
            motivoDespesa: { label: this.$t('todos'), id: 0 },
            motivoDespesas: [{ label: this.$t('todos'), id: 0 }],
            /**
             * @70209 - Adicionado na lista origem, destino e motivo despesa
             */
            fields: [
                { key: 'origem', width: null, sortable: true, label: '', footer: true },
                { key: 'destino', width: null, sortable: true, label: '', footer: true },
                { key: 'motivos-despesas', width: null, sortable: true, label: '', footer: true },
                { key: 'colaborador', width: null, sortable: true, label: '', footer: true },
                { key: 'valor', width: null, sortable: true, label: '', footer: true },
                { key: 'data-lancamento', width: null, sortable: true, label: '', footer: false },
                { key: 'situacao', width: 100, sortable: false, label: '', footer: false },
            ],
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
            },
            msgs: [],
            adiantamentos: [],
            service: new RelatorioKmAutomaticoService(this.$http, this.$router),
            debouncedOnSearch: Function,
            filtros: {
                search: '',
                datainicial: null,
                datafinal: null,
                sort: 'datahora',
                order: 'desc',
                situacao: null,
                idempresa: null,
                idusuario: null,
                descricaodespesa: null,
            },
            isLoading: true,
            isEmpty: false,
            valorKmsAutomaticos: 0,
            datainicial: (() => {
                let trintaDiasAtras = new Date();
                trintaDiasAtras.setDate(trintaDiasAtras.getDate() - 31);
                return trintaDiasAtras;
            })(),
            datafinal: new Date(),
            delete: null,
            permissao: {},
            firstTime: true,
            isExporting: false,
            noData: false,
            kmsautomaticos: [],
            isShowingKmAutomatico: false,
            idKmAutomatico: 0,
        };
    },
    created() {
        this.pagination.perPage = this.pagination.perPageOptions[this.pagination.optionStarted];
        this.permissao = this.hasPermissionAccess(
            'despesas',
            'relatorios',
            'des-relatorio-km-automatico'
        );
        if (!this.permissao.read) {
            this.$router.push({
                name: 'acesso-nao-permitido',
                params: { tela: 'relatorio-km-automatico' },
            });
        } else {
            this.debouncedOnSearch = this.debounce(this.onSearch, 500);
            this.carregarDadosByAss(1);
            this.filtroEmpresas();
            this.filtroMotivoDespesas();
            this.filtroColaboradores();
        }
    },
    methods: {
        atualizaFiltros() {
            this.filtros.datainicial = this.datainicial
                ? this.$moment(this.datainicial).format('YYYY-MM-DD')
                : null;
            this.filtros.datafinal = this.datafinal
                ? this.$moment(this.datafinal).add(1, 'days').format('YYYY-MM-DD')
                : null;
            this.filtros.situacao = this.situacao.id;
            this.filtros.idempresa = this.empresa.id;
            this.filtros.idusuario = this.colaborador.id;
            this.filtros.descricaodespesa = this.motivoDespesa.label.toUpperCase();
        },
        getReportPDF() {
            this.isLoading = true;

            this.atualizaFiltros();

            this.service
                .findAllExport(this.filtros)
                .then((value) => {
                    let filename =
                        'Km-automático-' + this.$moment().format('DD-MM-YYYY_HH-mm-ss') + '.pdf';
                    let blob = new Blob([value], { type: 'application/pdf' });
                    let link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.setAttribute('download', filename);
                    link.click();
                    setTimeout(function () {
                        window.URL.revokeObjectURL(blob);
                    }, 100);
                })
                .catch((e) => {
                    this.$root.showMessage('error', 'msg-erro-load');
                    console.error(e);
                })
                .finally((_) => {
                    this.isLoading = false;
                });
        },
        getReportCSV() {
            if (!this.isExporting) {
                this.isLoading = true;
                this.isExporting = true;
                if (this.firstTime) this.firstTime = false;
                this.service
                    .getCSVReport(this.filtros)
                    .then(this.downloadCSV)
                    .catch((e) => {
                        console.error('Erro ao trazer dados para o csv.', e);
                        this.showMessage('error', 'msg-erro-relatorio');
                        this.noData = true;
                    })
                    .finally(() => {
                        this.isExporting = false;
                        this.isLoading = false;
                    });
            }
        },
        downloadCSV(csv) {
            let filename, link, blob, url;
            if (csv == null) return;
            blob = new Blob(['\ufeff', csv], {
                encoding: 'UTF-8',
                type: 'data:text/csv;charset=utf-8;',
            });
            url = URL.createObjectURL(blob);
            filename = 'Km-automático-' + this.$moment().format('DD-MM-YYYY_HH-mm-ss') + '.csv';
            link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        },
        filtroEmpresas() {
            this.service
                .filtroEmpresas()
                .then((body) => {
                    if (body.content != null) {
                        return body.content.map((m) => {
                            return { id: m.empresa.idempresa, label: m.empresa.nome };
                        });
                    } else {
                        return null;
                    }
                })
                .then((res) => {
                    if (res != null) this.empresas.push(...res);
                })
                .catch(console.error);
        },
        filtroColaboradores() {
            this.service
                .filtroColaboradores()
                .then((body) => {
                    if (body.content != null) {
                        return body.content.map((m) => {
                            return { id: m.usuario.idusuario, label: m.usuario.nome };
                        });
                    } else {
                        return null;
                    }
                })
                .then((res) => {
                    if (res != null) this.colaboradores.push(...res);
                })
                .catch(console.error);
        },
        filtroMotivoDespesas() {
            this.service
                .filtroMotivoDespesas()
                .then((body) => {
                    if (body.content != null) {
                        let cont = 1;
                        return body.content.map((m) => {
                            return { id: cont++, label: m.descricaodespesa };
                        });
                    } else {
                        return null;
                    }
                })
                .then((res) => {
                    if (res != null) this.motivoDespesas.push(...res);
                })
                .catch(console.error);
        },

        onCleanSituacoes(val) {
            if (!val) this.situacao = this.situacoes[0];
        },
        onCleanEmpresas(val) {
            if (!val) this.empresa = this.empresas[0];
        },
        onCleanColaboradores(val) {
            if (!val) this.colaborador = this.colaboradores[0];
        },
        onCleanMotivoDespesas(val) {
            if (!val) this.motivoDespesa = this.motivoDespesas[0];
        },
        onCleanFilters() {
            this.situacao = this.situacoes[0];
            this.empresa = this.empresas[0];
            this.colaborador = this.colaboradores[0];
            this.motivoDespesa = this.motivoDespesas[0];
            this.limparBusca = true;
            this.filtros.search = '';
            this.datainicial = (() => {
                let trintaDiasAtras = new Date();
                trintaDiasAtras.setDate(trintaDiasAtras.getDate() - 31);
                return trintaDiasAtras;
            })();
            this.datafinal = new Date();
            this.timeoutCarregarDados();
        },
        timeoutCarregarDados(time = 600) {
            this.showFiltros = false;
            setTimeout(() => {
                this.carregarDadosByAss();
                this.limparBusca = false;
            }, time);
        },
        updatePagination(value) {
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDadosByAss(value.currentPage);
        },
        // Recebe o $emit do VerifiedDatePicker, adiciona o valor na variavel local que retorna para a prop do VerifiedDatePiker
        changeStartDate(date) {
            this.datainicial = date;
            this.carregarDadosByAss();
        },
        changeEndDate(date) {
            this.datafinal = date;
            this.carregarDadosByAss();
        },
        carregarDados(currentPage = 1) {
            if (currentPage == 1 && this.pagination.currentPage != 1)
                this.pagination.resetPagination = true;
            this.isLoading = true;

            this.atualizaFiltros();

            this.service
                .findAll(currentPage - 1, this.pagination.perPage, this.filtros)
                .then(this.mapItems)
                .then((value) => {
                    this.kmsautomaticos = value;
                    this.isEmpty = this.kmsautomaticos.length <= 0;
                })
                .catch((e) => {
                    this.isEmpty = true;
                    this.$root.showMessage('error', 'msg-erro-load');
                    console.error('Erro ao carregar os adiantamentos.');
                })
                .finally((_) => {
                    this.pagination.resetPagination = false;
                    this.isLoading = false;
                    this.getTotalValue();
                });
        },
        carregarDadosByAss(currentPage = 1) {
            if (currentPage == 1 && this.pagination.currentPage != 1)
                this.pagination.resetPagination = true;
            this.isLoading = true;

            this.atualizaFiltros();

            this.service
                .findAllByAss(currentPage - 1, this.pagination.perPage, this.filtros)
                .then(this.mapItems)
                .then((value) => {
                    this.kmsautomaticos = value;
                    this.isEmpty = this.kmsautomaticos.length <= 0;
                })
                .catch((e) => {
                    this.isEmpty = true;
                    this.$root.showMessage('error', 'msg-erro-load');
                    console.error('Erro ao carregar os adiantamentos.');
                })
                .finally((_) => {
                    this.pagination.resetPagination = false;
                    this.isLoading = false;
                });

            this.service
                .getValorTotal(currentPage - 1, this.pagination.perPage, this.filtros)
                .then((r) => r.content.map((item) => (this.valorKmsAutomaticos = item)));
        },
        /**
         * @70209 - Calcular valor total das despesas por pagina
         */
        getTotalValue() {
            this.valorKmsAutomaticos = 0;
            this.kmsautomaticos.forEach((kms) => {
                this.valorKmsAutomaticos += kms.valor;
            });
        },
        mapItems(body) {
            this.pagination.totalRows = body.totalElements;
            return body.content.map((m) => {
                return {
                    iddespesa: m.idDespesa,
                    origem: m.clienteCheckin.nome,
                    destino: m.clienteCheckout.nome,
                    despesamotivo: m.despesaMotivo.descricao,
                    colaborador: m.usuario.nome,
                    valor: m.valor,
                    'data-lancamento': m.datahorainc,
                    situacao: m.idAcerto == 0 ? 'ABERTO' : 'FECHADO',
                };
            });
        },
        //DELETAR
        promptForDelete(row) {
            this.delete = row;
            this.$root.confirm({
                message: this.$t('msg-confirmacao-desativacao-adiantamento-n', 
                        { n: row.colaborador, v: this.getDecimal(row.valor, null, true) }
                    ),
                onConfirm: () => this.promptForMotivoDelete(row),
                onCancel: this.removePrompt,
            });
        },
        promptForMotivoDelete(row) {
            this.$root.prompt({
                message: this.$t('msg-motivo-desativacao-adiantamento-n', 
                        { n: row.colaborador, v: this.getDecimal(row.valor, null, true) }
                    ),
                confirmText: this.$t('confirmar'),
                onConfirm: this.deletarAdiantamento,
                cancelText: this.$t('cancelar'),
                onCancel: this.removePrompt,
            });
        },
        showKmAutomaticoView(id) {
            this.isShowingKmAutomatico = !this.isShowingKmAutomatico;
            this.idKmAutomatico = id;
        },
        removePrompt() {
            this.delete = null;
        },
        isStatusButtonType(status) {
            if (status == 'ABERTO') return 'is-warning';
            else if (status == 'FECHADO') return 'is-success';
            else if (status == 'EXCLUIDO') return 'is-danger';
        },
        parseValueSearch(text) {
            //Função para deixar o valor no formato correto para api
            text = text.replace('.', '');
            text = text.replace(/[\,]+/g, '.');
            return text;
        },
        onSearch(text) {
            text = this.parseValueSearch(text);
            this.filtros.search = this.removeAcentos(text);
            this.carregarDadosByAss();
        },
        sortTable(field, order) {
            this.filtros.sort = field;
            this.filtros.order = order;
            this.carregarDadosByAss();
        },
        updateSituacao(situacao) {
            if (situacao != null)
                this.situacao = Object.assign(...this.situacoes.filter((s) => s.id == situacao.id));
            else this.situacao = Object.assign(...this.situacoes.filter((s) => s.id == 3));
        },
        parseDate(date) {
            return this.$moment(date, 'DD-MM-YYYY').toDate();
        },
        showMessage(type, message) {
            this.msgs.push(new Message(type, message, true));
        },
    },
};
</script>
<style lang="scss" scoped>
#main {
    padding: 0 20px;
    min-height: 100%;
    height: 100%;

    .page {
        position: relative;
        padding-left: 0;
        min-height: 100%;
        height: inherit;
        .calendar-btn {
            padding: 0;
            width: 100px;
            height: 35px;
        }
        .datepicker input {
            height: 40px !important;
        }
        .mt-3 {
            margin-top: 5rem !important;
        }
        .rodape-table-info {
            height: 100%;
        }
        .margin-none {
            margin: 0 !important;
        }
        .nav.nav-tabs {
            margin-bottom: 0;
            margin-left: -20px;
            padding-left: 20px;
        }
        .table-tag-full-width {
            width: 100%;
        }
    }
    $width: 400px;
    .filtros-right {
        z-index: 100;
        padding: 15px;
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
        background-color: $darker-bg-color;
        width: $width;
        height: 80%;
        position: fixed;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        border-top: solid 1px #d9d9d9;
        border-left: solid 1px #d9d9d9;
        border-bottom: solid 1px #d9d9d9;
        transition: 0.6s;
        -webkit-box-shadow: 0px 0px 25px 0px rgba(138, 138, 138, 0.8);
        box-shadow: 0px 0px 25px 0px rgba(138, 138, 138, 0.8);

        .filtros-container {
            margin-bottom: 60px;
        }

        .filtros-footer {
            border-bottom-left-radius: 5px;
            background-color: #fff;
            position: absolute;
            padding: 10px;
            bottom: 0;
            left: 0;
            right: 0;
            -webkit-box-shadow: 0px 0px 7px 0px rgba(153, 153, 153, 0.4);
            box-shadow: 0px 0px 7px 0px rgba(153, 153, 153, 0.4);

            .is-silver {
                color: $semi-title-color;
                box-shadow: inset 0px 0px 2px $dark-gray-color-opacity;
                @include exclude-button();
            }
        }
    }
    .hide-filtros-right {
        margin-right: -$width;
    }
    .mt-2-custom {
        margin-top: 1.5rem;
    }
    @media screen and (min-height: 850px) {
        .filtros-right {
            height: 50%;
        }
    }
    @media screen and (max-width: 640px) {
        .mt-2-custom {
            margin-top: 0;
        }
        $width: 95%;
        .filtros-right {
            width: $width;
        }
        .hide-filtros-right {
            margin-right: -$width;
        }
    }
}
</style>
